<template>
  <div class="notification-center">
    <el-page-header @back="goBack" content="互动消息" />
    
    <div class="header-section">
      <h2>互动消息</h2>
      <div class="header-actions">
        <el-button 
          :disabled="unreadCount === 0" 
          @click="markAllAsRead"
          type="primary"
          round
        >
          全部标记为已读
        </el-button>
        <el-popconfirm
          title="确认清空所有消息吗？此操作不可撤销！"
          @confirm="clearAllNotifications"
          width="260"
        >
          <template #reference>
            <el-button 
              :disabled="notifications.length === 0" 
              type="danger"
              round
            >
              清空消息
            </el-button>
          </template>
        </el-popconfirm>
      </div>
    </div>
    
    <div class="notifications-list">
      <el-card 
        v-for="item in notifications" 
        :key="item.id" 
        class="notification-item"
        :class="{ unread: item.isRead === 0 }"
        @click="viewNotification(item)"
      >
        <div class="notification-header">
          <el-avatar 
            :src="item.fromUserAvatar || '/default-avatar.png'" 
            :size="40" 
            class="user-avatar"
          />
          <div class="user-info">
            <div class="user-row">
              <span class="username">{{ item.fromUserName }}</span>
              <div class="action-type">
                <el-tag 
                  v-if="item.type === 1" 
                  type="warning" 
                  size="small"
                  effect="light"
                >
                  点赞了你的帖子
                </el-tag>
                <el-tag 
                  v-else-if="item.type === 2" 
                  type="warning" 
                  size="small"
                  effect="light"
                >
                  点赞了你的评论
                </el-tag>
                <el-tag 
                  v-else-if="item.type === 3" 
                  type="success" 
                  size="small"
                  effect="light"
                >
                  评论了你的帖子
                </el-tag>
                <el-tag 
                  v-else-if="item.type === 4" 
                  type="success" 
                  size="small"
                  effect="light"
                >
                  回复了你的评论
                </el-tag>
                <el-tag 
                  v-else-if="item.type === 5" 
                  type="primary" 
                  size="small"
                  effect="light"
                >
                  关注了你
                </el-tag>
                <el-tag 
                  v-else-if="item.type === 6" 
                  type="danger" 
                  size="small"
                  effect="light"
                >
                  收藏了你的帖子
                </el-tag>
              </div>
            </div>
            <div class="time">{{ formatDate(item.createTime) }}</div>
          </div>
        </div>
        <div class="notification-content" v-if="item.content">
          {{ item.content }}
        </div>
        <div class="actions">
          <el-button 
            size="small" 
            @click.stop="deleteNotification(item.id)"
            type="danger"
            link
          >
            删除
          </el-button>
        </div>
      </el-card>
      
      <div v-if="loading" class="loading">
        <div v-for="n in 3" :key="n" class="skeleton-item">
          <el-skeleton :rows="3" animated />
        </div>
      </div>
      
      <div v-if="notifications.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无互动消息" />
      </div>
    </div>
    
    <el-pagination
      v-if="total > 0"
      class="pagination"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="currentPage"
      :page-sizes="[10, 20, 30, 50]"
      :page-size="pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
    />
    
    <!-- 用户信息弹窗 -->
    <OtherUserProfileModal
      v-model="showUserProfileModal"
      :userId="selectedUserId"
      @close="handleUserProfileClose"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useRouter } from 'vue-router';
import { notificationApi } from '@/user/api/notification';
import { NotificationVO } from '@/user/api/notification';
import { formatDate } from '@/utils/date';
import OtherUserProfileModal from '@/user/views/user/OtherUserProfileModal.vue';

export default defineComponent({
  components: {
    OtherUserProfileModal
  },
  name: 'NotificationCenterView',
  setup() {
    // --- 响应式数据 ---
    const notifications = ref<NotificationVO[]>([]); // 通知列表
    const loading = ref(false); // 加载状态
    const currentPage = ref(1); // 当前页码
    const pageSize = ref(10); // 每页数量
    const total = ref(0); // 总数
    const unreadCount = ref(0); // 未读消息数
    const router = useRouter();
    const showUserProfileModal = ref(false); // 控制用户信息模态框显示
    const selectedUserId = ref(0); // 当前选中的用户ID

    const loadNotifications = async () => {
      loading.value = true;
      try {
        const response: any = await notificationApi.getNotificationList(currentPage.value, pageSize.value);

        console.log('[NotificationCenter] Raw response:', response);
        
        // 响应现在直接包含分页数据（因为request.ts中响应拦截器已处理）
        if (response && 'records' in response && 'total' in response) {
          console.log('[NotificationCenter] Using pagination data structure:', response);
          notifications.value = response.records || [];
          total.value = response.total || 0;
          
          console.log('[NotificationCenter] Loaded notifications:', notifications.value.length, 'total:', total.value);
        } else {
          // 如果响应结构不符合预期，设置默认值
          console.warn('[NotificationCenter] Unexpected response structure:', response);
          notifications.value = [];
          total.value = 0;
        }
      } catch (error) {
        console.error('[NotificationCenter] Error loading notifications:', error);
        ElMessage.error('获取消息列表失败');
        // 确保在出错时也设置默认值
        notifications.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const loadUnreadCount = async () => {
      console.log('[NotificationCenter] Loading unread count');
      try {
        const response: any = await notificationApi.getUnreadCount();
        console.log('[NotificationCenter] Unread count raw response:', response);
        
        // 修复数据结构访问错误，使用安全的访问方式
        // 根据日志，API返回格式为 {code: 1, msg: null, data: <number>}
        if (response && typeof response === 'object' && 'data' in response) {
          // 直接从response.data获取数值
          unreadCount.value = typeof response.data === 'number' ? response.data : Number(response.data);
        } else if (typeof response === 'number') {
          // 兼容直接返回数字的情况
          unreadCount.value = response;
        } else {
          unreadCount.value = 0;
        }
        
        console.log('[NotificationCenter] Updated unread count:', unreadCount.value);
      } catch (error) {
        console.error('[NotificationCenter] Error loading unread count:', error);
        ElMessage.error('获取未读消息数失败');
      }
    };

    const handleCurrentChange = (val: number) => {

      currentPage.value = val;
      loadNotifications();
    };

    const handleSizeChange = (val: number) => {

      pageSize.value = val;
      currentPage.value = 1;
      loadNotifications();
    };

    const viewNotification = async (item: NotificationVO) => {
      console.log('[NotificationCenter] Viewing notification:', item);
      
      // 如果是未读消息，标记为已读
      if (item.isRead === 0) {
        console.log('[NotificationCenter] Marking notification as read:', item.id);
        try {
          await notificationApi.markNotificationAsRead(item.id);
          item.isRead = 1;

          loadUnreadCount(); // 更新未读数
        } catch (error) {
          console.error('[NotificationCenter] Error marking as read:', error);
        }
      }
      
      // 根据消息类型跳转到相应页面
      switch(item.type) {
        case 1: // 点赞帖子
          // 跳转到帖子详情页
          console.log('[NotificationCenter] Type: Like post, ID:', item.targetId);
          router.push(`/post/${item.targetId}`);
          break;
        case 2: // 点赞评论
          // 跳转到帖子详情页
          console.log('[NotificationCenter] Type: Like comment, ID:', item.targetId);
          router.push(`/post/${item.targetId}`);
          break;
        case 3: // 评论帖子
          // 跳转到帖子详情页
          console.log('[NotificationCenter] Type: Comment post, ID:', item.targetId);
          router.push(`/post/${item.targetId}`);
          break;
        case 4: // 回复评论
          // 跳转到帖子详情页
          // console.log('[NotificationCenter] Type: Reply comment, ID:', item.targetId);
          // router.push(`/post/${item.targetId}`);
          break;
        case 5: // 关注用户
          // 直接显示关注者的个人主页（fromUserId）
          console.log('[NotificationCenter] Type: Follow user, follower ID:', item.fromUserId);
          selectedUserId.value = item.fromUserId;
          showUserProfileModal.value = true;
          break;
        case 6: // 收藏帖子
          // 跳转到帖子详情页
          console.log('[NotificationCenter] Type: Favorite post, ID:', item.targetId);
          router.push(`/post/${item.targetId}`);
          break;
      }
    };

    const markAllAsRead = async () => {
      try {
        await notificationApi.markAllNotificationsAsRead();
        ElMessage.success('全部标记为已读');
        loadNotifications();
        loadUnreadCount();
      } catch (error) {
        ElMessage.error('批量标记已读失败');
        console.error('[NotificationCenter] Error in mark all as read:', error);
      }
    };

    const deleteNotification = async (id: number) => {
      try {
        await notificationApi.deleteNotification(id);
        ElMessage.success('删除成功');
        loadNotifications();
        loadUnreadCount();
      } catch (error) {
        ElMessage.error('删除失败');
        console.error('[NotificationCenter] Error deleting notification:', error);
      }
    };

    const clearAllNotifications = async () => {
      try {
        await notificationApi.clearAllNotifications();
        ElMessage.success('清空成功');
        loadNotifications();
        loadUnreadCount();
      } catch (error) {
        ElMessage.error('清空失败');
        console.error('[NotificationCenter] Error clearing all notifications:', error);
      }
    };

    const goBack = () => {
      window.history.back();
    };

    // 处理用户资料模态框关闭
    const handleUserProfileClose = () => {
      showUserProfileModal.value = false;
    };

    const formatDateMethod = (dateString: string) => {
      return formatDate(dateString);
    };

    onMounted(() => {
      loadNotifications();
      loadUnreadCount();
    });

    return {
      notifications,
      currentPage,
      pageSize,
      total,
      loading,
      unreadCount,
      loadNotifications,
      handleCurrentChange,
      handleSizeChange,
      viewNotification,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      goBack,
      formatDate: formatDateMethod,
      showUserProfileModal,
      selectedUserId,
      handleUserProfileClose
    };
  }
});
</script>

<style>
.notification-center {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  background: linear-gradient(135deg, #fefefe 0%, #f8f3eb 100%);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(139, 125, 107, 0.15);
  position: relative;
  overflow: hidden;
}

/* 背景纹理 */
.notification-center::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(#d4c19c 1px, transparent 1px),
    radial-gradient(#d4c19c 1px, transparent 1px);
  background-size: 40px 40px;
  background-position: 0 0, 20px 20px;
  opacity: 0.08;
  z-index: 0;
  pointer-events: none;
}

.notification-center > * {
  position: relative;
  z-index: 1;
}

.notification-center .header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9dcc8;
}

.notification-center .header-section h2 {
  margin: 0;
  color: #5c5142;
  font-size: 24px;
  font-weight: 600;
  font-family: 'Quicksand', 'Nunito', 'Microsoft YaHei', sans-serif;
}

.notification-center .header-section .header-actions .el-button {
  margin-left: 10px;
  border-radius: 20px;
  padding: 8px 20px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(212, 131, 65, 0.2);
  transition: all 0.3s ease;
}

.notification-center .header-section .header-actions .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 131, 65, 0.3);
}

.notification-center .notifications-list .notification-item {
  margin-bottom: 15px;
  cursor: pointer;
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.08);
  transition: all 0.3s ease;
  background: #fff;
  border: 1px solid #f0e6d2;
  padding: 16px;
}

.notification-center .notifications-list .notification-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(139, 125, 107, 0.15);
}

.notification-center .notifications-list .notification-item.unread {
  background: linear-gradient(to right, #fff9f2 0%, #fefefe 100%);
  border-left: 4px solid #d48341;
}

.notification-center .notifications-list .notification-item .notification-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.notification-center .notifications-list .notification-item .notification-header .user-avatar {
  margin-right: 12px;
  border: 2px solid #f0e6d2;
  box-shadow: 0 2px 6px rgba(139, 125, 107, 0.15);
}

.notification-center .notifications-list .notification-item .notification-header .user-info {
  flex: 1;
}

.notification-center .notifications-list .notification-item .notification-header .user-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.notification-center .notifications-list .notification-item .notification-header .user-info .username {
  font-weight: 600;
  color: #5c5142;
  font-size: 16px;
}

.notification-center .notifications-list .notification-item .notification-header .user-info .action-type {
  margin-left: 10px;
}

.notification-center .notifications-list .notification-item .notification-header .time {
  font-size: 13px;
  color: #8B7D6B;
  margin-top: 2px;
}

.notification-center .notifications-list .notification-item .notification-content {
  color: #606266;
  line-height: 1.6;
  margin-bottom: 12px;
  padding-left: 52px; /* 为头像留出空间 */
  padding-top: 8px;
  border-left: 2px solid #f9f2eb;
  border-radius: 4px;
  background: #fafafa;
  padding: 8px 12px;
  margin-left: 52px;
  margin-top: 8px;
  border-radius: 0 8px 8px 8px;
}

.notification-center .notifications-list .notification-item .actions {
  text-align: right;
  padding-top: 8px;
  border-top: 1px dashed #eee;
  margin-top: 8px;
}

.notification-center .notifications-list .loading {
  margin: 20px 0;
}

.notification-center .notifications-list .loading .skeleton-item {
  margin-bottom: 15px;
  padding: 16px;
  border-radius: 12px;
  background: white;
}

.notification-center .notifications-list .empty-state {
  text-align: center;
  padding: 60px 20px;
}

.notification-center .pagination {
  text-align: center;
  margin-top: 20px;
}

.notification-center .pagination .el-pager li {
  transition: all 0.3s ease;
}

.notification-center .pagination .el-pager li:hover {
  color: #d48341;
}

.notification-center .pagination .el-pager li.active {
  color: #d48341;
  font-weight: 600;
}
</style>